import { type FC, memo } from "react";
import { generatePath, useLocation } from "react-router-dom";

import { cx } from "@emotion/css";
import { SearchableCard } from "@snapchat/snap-design-system";
import { useIntl } from "react-intl";
import { areEqual } from "react-window";

import { type PaginationFilter } from "../../core/pagination/models";
import { AbsoluteRoute } from "../../core/routes";
import { type MyLensesLens } from "../../gql-generated/types";
import { ListContainer, PageContentSpinner, SearchContainer } from "../../layout/components";
import {
    IconSection,
    LensContainer,
    LensIcon,
    LensStatusWithWarning,
    LensUpdateAtDate,
    lensDataContainerStyles,
    tableTextStyle,
} from "../../lens/components/shared";
import { useLenses } from "../../lenses/hooks";
import { LoadableItem, TruncateComponent, VirtualizedList } from "../../shared/components";
import { type VirtualizedListItemProps } from "../../shared/models";
import { PaginationAtomsKeys } from "../../shared/state";
import { LinkStyled } from "../../shared/styles";
import { ListSizeConfig, iconWidth, lensNameWidth, statusWidth } from "../../submit/components/lens-for-update/shared";

import styles from "./WebLensPage.module.css";
import { messages } from "./messages.generated";

// Server-side filter, same as the Studio picker: a locked lens is mid-processing and Lens
// Central rejects updates to it, so the picker never offers one.
const PICKER_FILTER: Partial<PaginationFilter> = { locked: false };

interface PickerRowData {
    orgId: string;
    /** The chooser's own query string, carried so the update page can link back to this list. */
    search: string;
}

// The Studio picker's LensRow can't be reused here: its link wrapper resolves the Studio
// submission route (requiring :submissionId) and it runs Studio-only machinery per row
// (submission-errors query, destinations tooltip, restrictions modal). This row keeps the
// same shared atoms and column widths, linking to the web-lens update page instead.
const PickerRow = memo<VirtualizedListItemProps<MyLensesLens, PickerRowData>>(
    ({ index, style, data: { list, data } }) => {
        const lens = list[index];
        return (
            <LoadableItem style={style} data={lens} skeletonHeight={16}>
                {lens && data ? (
                    <LensContainer style={style} id={lens.id} data-testid="web-lens-update-picker-row">
                        <LinkStyled
                            className={lensDataContainerStyles}
                            to={{
                                pathname: generatePath(AbsoluteRoute.WEB_LENSES_UPDATE, {
                                    orgId: data.orgId,
                                    lensId: lens.id,
                                }),
                                search: data.search,
                            }}
                        >
                            <IconSection className={iconWidth}>
                                <LensIcon src={lens.iconUrl} />
                            </IconSection>
                            <TruncateComponent className={cx(tableTextStyle, lensNameWidth)} content={lens.name} />
                            <LensUpdateAtDate seconds={lens.updatedAt?.seconds ?? 0} />
                            <div className={statusWidth}>
                                <LensStatusWithWarning
                                    status={lens.status}
                                    hasSubmissionErrors={false}
                                    destinations={lens.destinations}
                                    showDestinationsText={false}
                                    hasDistributionRestrictions={false}
                                    loading={false}
                                />
                            </div>
                        </LinkStyled>
                    </LensContainer>
                ) : (
                    <></>
                )}
            </LoadableItem>
        );
    },
    areEqual,
);

interface WebLensUpdatePickerListProps {
    orgId: string;
    folderId: string;
}

/**
 * The start page's update mode: a searchable, virtualized list of the selected folder's lenses,
 * riding the same getLensesForUpdate machinery as the Studio picker (own pagination key, so the
 * two pickers' search state never bleed into each other). Clicking a lens opens the web-lens
 * update page for it.
 */
export const WebLensUpdatePickerList: FC<WebLensUpdatePickerListProps> = ({ orgId, folderId }) => {
    const intl = useIntl();
    const location = useLocation();
    const { lenses, isFirstFetching, isReFetching, loadMore, hasMore, lensSearchText, setLensSearchText } = useLenses({
        filterByOptions: PICKER_FILTER,
        lensSpaceId: folderId,
        paginationAtomsKey: PaginationAtomsKeys.WebLensUpdatePicker,
        forUpdate: true,
    });

    if (isFirstFetching) {
        return <PageContentSpinner height="calc(50vh - 56px)" />;
    }

    return (
        <ListContainer className={styles.pickerList}>
            <SearchableCard
                searchPlaceholder={intl.formatMessage(messages.searchLensesPlaceholder)}
                padBody={false}
                onSearch={setLensSearchText}
                searchValue={lensSearchText}
            >
                <SearchContainer loading={isReFetching} noResults={lenses.length === 0} height={ListSizeConfig.height}>
                    <VirtualizedList
                        data={{ list: lenses, data: { orgId, search: location.search } }}
                        renderMore={loadMore}
                        hasMore={hasMore}
                        itemSize={ListSizeConfig.lensHeight}
                        height={ListSizeConfig.height}
                        width="100%"
                    >
                        {PickerRow}
                    </VirtualizedList>
                </SearchContainer>
            </SearchableCard>
        </ListContainer>
    );
};
